import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  submitFeedback,
  OrgMatchPreferenceSuggestion,
  PreferenceModification,
} from '../api/feedbackAPI'
import {
  saveMatchPreferences,
  updateOrgPreference,
  SavePreferenceItem,
} from '../api/orgsAPI'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Checkbox,
  Divider,
  Typography,
  Modal,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import OrgMatchPreferences from '@/components/OrgMatchPreferences'

const FEEDBACK_CATEGORIES = [
  {
    value: 'applied_received_grant',
    label: "I've applied to/received a grant from this funder already",
  },
  { value: 'incorrect_info', label: 'The information is incorrect' },
  {
    value: 'dislike',
    label: "I don't like this opportunity",
  },
  { value: 'bad_match', label: "This isn't a good match" },
  { value: 'outdated_questions', label: 'Funder updated their questions' },
]
const BAD_MATCH_CATEGORIES = [
  {
    value: 'unfit_for_funder',
    label: "Close match, but we don't fit the criteria",
  },
  { value: 'complete_mismatch', label: 'Misaligned' },
  {
    value: 'wrong_location',
    label: 'Wrong location',
  },
  {
    value: 'grant_size',
    label: 'This grant is too small/large',
  },
]
const INCORRECT_INFO_CATEGORIES = [
  {
    value: 'fund_closed',
    label: 'Fund is closed/unavailable',
  },
  {
    value: 'outdated_info',
    label: 'The information is out of date',
  },
  {
    value: 'location_incorrect',
    label: 'The location for funding is not correct',
  },
]

type PreferenceSelection = 'org' | 'need' | 'skip'

interface FeedbackModalProps {
  isOpen: boolean
  onClose: () => void
  scanResultId: number
  orgUuid: string
  needId?: number
  onFeedbackSubmitted?: () => void
  token: string
}

const FeedbackNegativeModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  scanResultId,
  orgUuid,
  needId,
  onFeedbackSubmitted,
  token,
}) => {
  const [subject, setSubject] = useState('')
  const [category, setCategory] = useState<string | undefined>(undefined)
  const [message, setMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showBadMatchInputs, setShowBadMatchInputs] = useState(false)
  const [badMatchCategory, setBadMatchCategory] = useState<string | undefined>(
    undefined,
  )
  const [badMatchSubject, setBadMatchSubject] = useState<string>('')
  const [showIncorrectInfoInputs, setShowIncorrectInfoInputs] = useState(false)
  const [incorrectInfoCategory, setIncorrectInfoCategory] = useState<
    string | undefined
  >(undefined)
  const [incorrectInfoSubject, setIncorrectInfoSubject] = useState<string>('')
  const [matchPreferenceSuggestion, setMatchPreferenceSuggestion] =
    useState<OrgMatchPreferenceSuggestion | null>(null)
  const [preferenceSelections, setPreferenceSelections] = useState<
    Record<number, PreferenceSelection>
  >({})
  const [modificationSelections, setModificationSelections] = useState<
    Record<number, boolean>
  >({})

  const [isSavingPreferences, setIsSavingPreferences] = useState(false)
  const [preferencesSaved, setPreferencesSaved] = useState(false)
  const [preferenceError, setPreferenceError] = useState<string | null>(null)

  useEffect(() => {
    setShowBadMatchInputs(category === 'bad_match')
    setShowIncorrectInfoInputs(category === 'incorrect_info')
    if (category !== 'bad_match') {
      setBadMatchCategory(undefined)
      setBadMatchSubject('')
    }
    if (category !== 'incorrect_info') {
      setIncorrectInfoCategory(undefined)
      setIncorrectInfoSubject('')
    }
  }, [category])

  if (!isOpen) {
    return null
  }

  function getFeedbackCategory(subject: string) {
    const categoryValue = FEEDBACK_CATEGORIES.find(
      (category) => category.value === subject,
    )

    if (categoryValue) {
      return categoryValue.value
    } else {
      return undefined
    }
  }

  function getBadMatchCategory(input: string) {
    const categoryValue = BAD_MATCH_CATEGORIES.find(
      (category) => category.value === input,
    )

    if (categoryValue) {
      return categoryValue.value
    } else {
      return undefined
    }
  }

  function getBadMatchSubject(categoryValue: string) {
    const subjectLabel = BAD_MATCH_CATEGORIES.find(
      (category) => category.value === categoryValue,
    )

    if (subjectLabel) {
      return subjectLabel.label
    } else {
      return ''
    }
  }
  function getFeedbackSubject(categoryValue: string) {
    const subjectLabel = FEEDBACK_CATEGORIES.find(
      (category) => category.value === categoryValue,
    )

    if (subjectLabel) {
      return subjectLabel.label
    } else {
      return ''
    }
  }

  function handleCategoryChange(e: SelectChangeEvent<string>) {
    e.preventDefault()
    setSubject(e.target.value as string)
    const categoryValue = getFeedbackCategory(e.target.value as string)

    setCategory(categoryValue)
  }

  function getIncorrectInfoCategory(input: string) {
    return INCORRECT_INFO_CATEGORIES.find((c) => c.value === input)?.value
  }

  function getIncorrectInfoSubject(categoryValue: string) {
    return (
      INCORRECT_INFO_CATEGORIES.find((c) => c.value === categoryValue)?.label ??
      ''
    )
  }

  function handleIncorrectInfoCategoryChange(e: SelectChangeEvent<string>) {
    e.preventDefault()
    setIncorrectInfoSubject(e.target.value as string)
    setIncorrectInfoCategory(getIncorrectInfoCategory(e.target.value as string))
  }

  function handleBadMatchCategoryChange(e: SelectChangeEvent<string>) {
    e.preventDefault()
    setBadMatchSubject(e.target.value as string)
    const categoryValue = getBadMatchCategory(e.target.value as string)
    setBadMatchCategory(categoryValue)
  }

  const handlePreferenceChange = (
    index: number,
    value: PreferenceSelection,
  ) => {
    setPreferenceSelections((prev) => {
      if (prev[index] === value) {
        const next = { ...prev }
        delete next[index] // deselect the option
        return next
      }
      return { ...prev, [index]: value }
    })
  }

  const handleModificationToggle = (preferenceId: number) => {
    setModificationSelections((prev) => ({
      ...prev,
      [preferenceId]: !prev[preferenceId],
    }))
  }

  const handleSavePreferences = async () => {
    if (!matchPreferenceSuggestion) return

    const preferences: SavePreferenceItem[] = []

    matchPreferenceSuggestion.preference_suggestions.forEach(
      (suggestion, index) => {
        const selection = preferenceSelections[index]
        if (selection === 'org') {
          preferences.push({ preference_text: suggestion })
        } else if (selection === 'need' && needId) {
          preferences.push({ preference_text: suggestion, need_id: needId })
        }
      },
    )

    const selectedModifications = (
      matchPreferenceSuggestion.preference_modifications ?? []
    ).filter((mod) => modificationSelections[mod.preference_id])

    if (preferences.length === 0 && selectedModifications.length === 0) return

    setIsSavingPreferences(true)
    setPreferenceError(null)

    try {
      if (preferences.length > 0) {
        await saveMatchPreferences(orgUuid, preferences, token)
      }
      for (const mod of selectedModifications) {
        await updateOrgPreference(
          orgUuid,
          mod.preference_id,
          mod.suggested_text,
          token,
        )
      }
      setPreferencesSaved(true)
    } catch {
      setPreferenceError('Failed to save preferences')
    } finally {
      setIsSavingPreferences(false)
    }
  }

  const handleClosePreferences = () => {
    setMatchPreferenceSuggestion(null)
    setPreferenceSelections({})
    setModificationSelections({})
    setPreferenceError(null)
    setPreferencesSaved(false)
    onClose()
    if (onFeedbackSubmitted) {
      onFeedbackSubmitted()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!subject.trim()) {
      setError('Please choose a category')
      return
    }

    if (showBadMatchInputs && !badMatchSubject.trim()) {
      setError("Please choose a sub category for why this isn't a good match")
      return
    }

    if (showIncorrectInfoInputs && !incorrectInfoSubject.trim()) {
      setError('Please choose a sub category for what information is incorrect')
      return
    }

    let subjectLabel = getFeedbackSubject(subject)

    if (category === 'bad_match') {
      subjectLabel += ` - ${getBadMatchSubject(badMatchSubject)}`
    }

    if (category === 'incorrect_info') {
      subjectLabel += ` - ${getIncorrectInfoSubject(incorrectInfoSubject)}`
    }

    setError(null)
    setIsSubmitting(true)

    try {
      const result = await submitFeedback({
        scan_result_id: scanResultId,
        org_uuid: orgUuid,
        subject: subjectLabel,
        category,
        bad_match_category: badMatchCategory,
        message,
        hidden: true, // Hide the match by default when giving feedback
        token,
      })

      // Reset form
      setSubject('')
      setMessage('')
      setCategory(undefined)
      setBadMatchCategory(undefined)
      setBadMatchSubject('')
      setIncorrectInfoCategory(undefined)
      setIncorrectInfoSubject('')

      if (result.preference_response) {
        setMatchPreferenceSuggestion(result.preference_response)
        return // don't close the modal
      }

      // Close modal and notify parent
      onClose()
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted()
      }
    } catch {
      setError('Failed to submit feedback')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={
        matchPreferenceSuggestion && !preferencesSaved
          ? undefined
          : handleClosePreferences
      }
      aria-labelledby="remove-match-modal"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: matchPreferenceSuggestion ? '700px' : '600px',
          maxWidth: '90vw',
          maxHeight: { xs: '70vh', sm: '90vh' },
          overflowY: 'auto',
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          px: { xs: 2, sm: 4 },
          borderRadius: 2,
          pt: 3,
          pb: 3,
        }}
      >
        {preferencesSaved ? (
          <>
            <Typography
              variant="h5"
              component="h2"
              sx={{ textAlign: 'center', mb: 2, fontWeight: 500 }}
            >
              Preferences Saved
            </Typography>

            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Your match preferences have been saved successfully.
              </Typography>
            </Alert>

            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              You can update or add new preferences any time in either the{' '}
              <Link
                to="/profile/match-preferences"
                style={{ color: '#1C3045' }}
              >
                Manage Organisation
              </Link>{' '}
              or{' '}
              <Link to="/funding-requests" style={{ color: '#1C3045' }}>
                Funding Requests
              </Link>{' '}
              page.
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                disableRipple
                variant="contained"
                onClick={handleClosePreferences}
                sx={{
                  bgcolor: '#1C3045',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '16px',
                }}
              >
                Close
              </Button>
            </Box>
          </>
        ) : matchPreferenceSuggestion ? (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Your feedback has been submitted successfully.
              </Typography>
            </Alert>
            <Typography
              variant="h5"
              component="h2"
              sx={{ textAlign: 'center', mb: 2, fontWeight: 500 }}
            >
              Would you like to apply these preferences?
            </Typography>

            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Based on your feedback, here are some suggested preferences to
              improve your future matches. For each suggestion, choose whether
              to apply it across your organisation or just to this particular
              funding request.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              The next time you Find Matches or Re-find Matches for this funding
              request, these preferences will be applied.
            </Typography>

            <Accordion
              disableGutters
              sx={{
                mb: 3,
                boxShadow: 'none',
                '&:before': { display: 'none' },
                border: '1px solid #757575',
                borderRadius: 1,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">
                  Why were these suggested?
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {matchPreferenceSuggestion.reasoning}
                </Typography>
              </AccordionDetails>
            </Accordion>

            {(matchPreferenceSuggestion.preference_modifications ?? []).length >
              0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Suggested updates to existing preferences
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 2, color: 'text.secondary' }}
                >
                  Based on your feedback, these existing preferences may need
                  refining.
                </Typography>
                {matchPreferenceSuggestion.preference_modifications.map(
                  (mod: PreferenceModification) => (
                    <Box
                      key={mod.preference_id}
                      sx={{
                        mb: 2,
                        p: 2,
                        border: '2px solid #757575',
                        borderRadius: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
                      >
                        {mod.need_id != null
                          ? 'Funding request preference'
                          : 'Organisation preference'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', display: 'block' }}
                      >
                        Current:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: 1,
                          color: 'text.secondary',
                          fontStyle: 'italic',
                        }}
                      >
                        {mod.original_text}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', display: 'block' }}
                      >
                        Suggested update:
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {mod.suggested_text}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            disableRipple
                            checked={
                              modificationSelections[mod.preference_id] ?? false
                            }
                            onChange={() =>
                              handleModificationToggle(mod.preference_id)
                            }
                            sx={{ '&.Mui-checked': { color: '#1C3045' } }}
                          />
                        }
                        label="Apply update"
                      />
                    </Box>
                  ),
                )}
                <Divider sx={{ mb: 3 }} />
              </>
            )}

            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Suggested new preferences
            </Typography>

            {preferenceError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2">{preferenceError}</Typography>
              </Alert>
            )}

            {matchPreferenceSuggestion.preference_suggestions.map(
              (suggestion, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: '2px solid #757575',
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {suggestion}
                  </Typography>
                  <RadioGroup row value={preferenceSelections[index] ?? ''}>
                    <FormControlLabel
                      value="org"
                      control={
                        <Radio
                          disableRipple
                          onClick={() => handlePreferenceChange(index, 'org')}
                          sx={{
                            '&.Mui-checked': { color: '#1C3045' },
                          }}
                        />
                      }
                      label="Organisation"
                    />
                    {needId && (
                      <FormControlLabel
                        value="need"
                        control={
                          <Radio
                            disableRipple
                            onClick={() =>
                              handlePreferenceChange(index, 'need')
                            }
                            sx={{
                              '&.Mui-checked': { color: '#1C3045' },
                            }}
                          />
                        }
                        label="This funding request only"
                      />
                    )}
                  </RadioGroup>
                </Box>
              ),
            )}

            <Typography
              variant="body2"
              sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}
            >
              Note: You can update or add new preferences any time in either the{' '}
              <Link to="/profile/funding-profile" style={{ color: '#1C3045' }}>
                Manage Organisation
              </Link>{' '}
              or{' '}
              <Link to="/funding-requests" style={{ color: '#1C3045' }}>
                Funding Requests
              </Link>{' '}
              page.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              You can also view and edit your current preferences for your
              organisation or this funding request below.
            </Typography>

            <Accordion
              disableGutters
              sx={{
                mb: 2,
                boxShadow: 'none',
                '&:before': { display: 'none' },
                border: '1px solid #757575',
                borderRadius: 1,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">
                  Current Organisation Preferences
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <OrgMatchPreferences orgUuid={orgUuid} token={token} compact />
              </AccordionDetails>
            </Accordion>

            {needId && (
              <Accordion
                disableGutters
                sx={{
                  mb: 2,
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  border: '1px solid #757575',
                  borderRadius: 1,
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    Current Funding Request Preferences
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <OrgMatchPreferences
                    orgUuid={orgUuid}
                    token={token}
                    needId={needId}
                    compact
                  />
                </AccordionDetails>
              </Accordion>
            )}

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'flex-end',
                mt: 2,
              }}
            >
              <Button
                variant="outlined"
                disableRipple
                onClick={handleClosePreferences}
                disabled={isSavingPreferences}
                sx={{
                  borderColor: '#1C3045',
                  border: 2,
                  borderRadius: 2,
                  color: '#1C3045',
                  textTransform: 'none',
                  fontSize: '16px',
                  '&:focus': {
                    backgroundColor: '#e8e8e8',
                  },
                }}
              >
                Skip All
              </Button>
              <Button
                disableRipple
                variant="contained"
                onClick={handleSavePreferences}
                disabled={
                  isSavingPreferences ||
                  (Object.keys(preferenceSelections).length === 0 &&
                    Object.values(modificationSelections).every((v) => !v))
                }
                sx={{
                  bgcolor: '#1C3045',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '16px',
                }}
              >
                {isSavingPreferences ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress
                      size={20}
                      sx={{ color: 'white', mr: 1 }}
                    />
                    <span>Saving...</span>
                  </Box>
                ) : (
                  'Save Preferences'
                )}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography
              variant="h5"
              component="h2"
              sx={{ textAlign: 'center', mb: 2, fontWeight: 500 }}
            >
              Remove Match
            </Typography>

            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              Giving feedback on why this match wasn't helpful will improve our
              database and AI recommendations for future matches.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">
                  Error submitting feedback
                </Typography>
                <Typography variant="body2">{error}</Typography>
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  If this problem persists, please contact support.
                </Typography>
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <FormControl sx={{ width: '100%', mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  id="feedback-category-label"
                  sx={{ mb: 1 }}
                >
                  Why was this match not helpful?
                </Typography>
                <Select
                  value={subject}
                  onChange={(e) => handleCategoryChange(e)}
                  disabled={isSubmitting}
                  aria-labelledby="feedback-category-label"
                  aria-required="true"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#757575',
                    },
                  }}
                >
                  {FEEDBACK_CATEGORIES.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {showBadMatchInputs && (
                <FormControl sx={{ width: '100%', mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    id="bad-match-category-label"
                    sx={{ mb: 1 }}
                  >
                    What made this a bad match?
                  </Typography>
                  <Select
                    value={badMatchSubject}
                    onChange={(e) => handleBadMatchCategoryChange(e)}
                    disabled={isSubmitting}
                    aria-labelledby="bad-match-category-label"
                    aria-required="true"
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#757575',
                      },
                    }}
                  >
                    {BAD_MATCH_CATEGORIES.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {showIncorrectInfoInputs && (
                <FormControl sx={{ width: '100%', mb: 2 }}>
                  <Typography
                    variant="subtitle2"
                    id="incorrect-info-category-label"
                    sx={{ mb: 1 }}
                  >
                    What information is incorrect?
                  </Typography>
                  <Select
                    value={incorrectInfoSubject}
                    onChange={(e) => handleIncorrectInfoCategoryChange(e)}
                    disabled={isSubmitting}
                    aria-labelledby="incorrect-info-category-label"
                    aria-required="true"
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#757575',
                      },
                    }}
                  >
                    {INCORRECT_INFO_CATEGORIES.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {incorrectInfoCategory === 'location_incorrect' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Have you checked what locations you have listed for your
                    organisation or this funding request?
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Council-level locations include suburb-specific
                    opportunities inside that council by default.
                  </Typography>
                  <Typography variant="body2">
                    If the council is too broad, refine your locations in
                    settings so future matches are more precise.
                  </Typography>
                </Alert>
              )}

              {badMatchCategory === 'wrong_location' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Council-level locations include suburb-specific
                    opportunities inside that council by default.
                  </Typography>
                  <Typography variant="body2">
                    Refine your organisation or funding request locations in
                    settings if this match is for a place you do not serve.
                  </Typography>
                </Alert>
              )}

              {badMatchCategory === 'grant_size' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Have you checked the minimum and maximum grant amounts set
                    on your funding request?
                  </Typography>
                  <Typography variant="body2">
                    Updating these will help us find better-sized matches for
                    you. You can edit your funding request on the{' '}
                    <Link to="/funding-requests" style={{ color: '#1C3045' }}>
                      Funding Requests
                    </Link>{' '}
                    page.
                  </Typography>
                </Alert>
              )}

              <FormControl sx={{ width: '100%', mb: 2 }}>
                <Typography variant="subtitle2">
                  Please provide more details{' '}
                  {category === 'bad_match' ||
                  category === 'dislike' ||
                  incorrectInfoCategory === 'outdated_info' ||
                  incorrectInfoCategory === 'location_incorrect'
                    ? '*'
                    : `(optional)`}
                </Typography>
                <Typography
                  id="message-helper-text"
                  variant="body2"
                  sx={{ mb: 1, color: 'text.secondary' }}
                >
                  Tell us more about what made this match inappropriate for your
                  organisation?
                </Typography>
                <TextField
                  required={
                    category === 'bad_match' ||
                    category === 'dislike' ||
                    incorrectInfoCategory === 'outdated_info' ||
                    incorrectInfoCategory === 'location_incorrect'
                  }
                  fullWidth
                  multiline
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={isSubmitting}
                  aria-describedby="message-helper-text"
                  sx={{
                    mb: 3,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#757575',
                    },
                  }}
                />
              </FormControl>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  disableRipple
                  onClick={onClose}
                  disabled={isSubmitting}
                  sx={{
                    borderColor: '#1C3045',
                    border: 2,
                    borderRadius: 2,
                    color: '#1C3045',
                    textTransform: 'none',
                    fontSize: '16px',
                    '&:focus': {
                      backgroundColor: '#e8e8e8',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  disableRipple
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting || !subject.trim()}
                  sx={{
                    bgcolor: '#1C3045',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '16px',
                  }}
                >
                  {isSubmitting ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CircularProgress
                        size={20}
                        sx={{ color: 'white', mr: 1 }}
                      />
                      <span>Submitting...</span>
                    </Box>
                  ) : (
                    'Remove & Submit Feedback'
                  )}
                </Button>
              </Box>
            </form>
          </>
        )}
      </Box>
    </Modal>
  )
}

export default FeedbackNegativeModal
